// FILE: threadDirectoryRouteSearch.ts
// Purpose: The thread directory's URL state — every filter, the sort and the page — parsed from
//          and written back to the route's search params.
// Layer: Route search-param contract
// Exports: ThreadDirectoryRouteSearch, parseThreadDirectoryRouteSearch, toThreadDirectoryInput
//
// Same shape as `pullRequestsRouteSearch.ts`, with one deliberate difference: the page number *is*
// part of the URL here. That section accumulates pages ("load more"), so its page count says
// nothing about what the reader is looking at; this one walks them, and "page 4 of 12" is a place
// somebody can be sent to.
//
// An unreadable value is dropped rather than rejected, per the same rule: a URL edited by hand
// should still show a list.

import type { ProjectId, ThreadDirectoryInput, UserId } from "@taskin-code/contracts";
import {
  THREAD_DIRECTORY_DEFAULT_PER_PAGE,
  THREAD_DIRECTORY_QUERY_MAX_LENGTH,
} from "@taskin-code/contracts";

/** Every state the filter offers, in the order it renders them. */
export const THREAD_DIRECTORY_STATES = ["active", "running", "archived", "all"] as const;
export type ThreadDirectoryStateFilter = (typeof THREAD_DIRECTORY_STATES)[number];

export const THREAD_DIRECTORY_SORTS = ["activity", "created"] as const;
export type ThreadDirectorySortOption = (typeof THREAD_DIRECTORY_SORTS)[number];

/** What the section shows when the URL says nothing. */
export const DEFAULT_THREAD_DIRECTORY_STATE: ThreadDirectoryStateFilter = "active";
export const DEFAULT_THREAD_DIRECTORY_SORT: ThreadDirectorySortOption = "activity";

export interface ThreadDirectoryRouteSearch {
  readonly q?: string | undefined;
  readonly projectId?: ProjectId | undefined;
  readonly userId?: UserId | undefined;
  readonly state?: ThreadDirectoryStateFilter | undefined;
  readonly pr?: "with" | "without" | undefined;
  /** Calendar days (`YYYY-MM-DD`), which is what a date input produces. */
  readonly activityFrom?: string | undefined;
  readonly activityTo?: string | undefined;
  readonly createdFrom?: string | undefined;
  readonly createdTo?: string | undefined;
  readonly sort?: ThreadDirectorySortOption | undefined;
  readonly page?: number | undefined;
}

const isState = (value: unknown): value is ThreadDirectoryStateFilter =>
  typeof value === "string" && (THREAD_DIRECTORY_STATES as ReadonlyArray<string>).includes(value);

const isSort = (value: unknown): value is ThreadDirectorySortOption =>
  typeof value === "string" && (THREAD_DIRECTORY_SORTS as ReadonlyArray<string>).includes(value);

function normalizeSearchString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

/** `YYYY-MM-DD` and nothing else: anything looser reaches the query as a bound nobody meant. */
function normalizeDay(value: unknown): string | undefined {
  const text = normalizeSearchString(value);
  return text !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : undefined;
}

function normalizePage(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(normalizeSearchString(value) ?? NaN);
  if (!Number.isFinite(parsed)) return undefined;
  const page = Math.floor(parsed);
  return page > 1 ? page : undefined;
}

export function parseThreadDirectoryRouteSearch(
  search: Record<string, unknown>,
): ThreadDirectoryRouteSearch {
  // Bounded here rather than at the request: the server caps it too, but an unbounded value off
  // the URL would travel over the socket on every keystroke of a paste.
  const q = normalizeSearchString(search.q)?.slice(0, THREAD_DIRECTORY_QUERY_MAX_LENGTH);
  const projectId = normalizeSearchString(search.projectId) as ProjectId | undefined;
  const userId = normalizeSearchString(search.userId) as UserId | undefined;
  const state = isState(search.state) ? search.state : undefined;
  const sort = isSort(search.sort) ? search.sort : undefined;
  const pr = search.pr === "with" || search.pr === "without" ? search.pr : undefined;
  const page = normalizePage(search.page);

  return {
    ...(q ? { q } : {}),
    ...(projectId ? { projectId } : {}),
    ...(userId ? { userId } : {}),
    ...(state && state !== DEFAULT_THREAD_DIRECTORY_STATE ? { state } : {}),
    ...(pr ? { pr } : {}),
    ...(normalizeDay(search.activityFrom)
      ? { activityFrom: normalizeDay(search.activityFrom) }
      : {}),
    ...(normalizeDay(search.activityTo) ? { activityTo: normalizeDay(search.activityTo) } : {}),
    ...(normalizeDay(search.createdFrom) ? { createdFrom: normalizeDay(search.createdFrom) } : {}),
    ...(normalizeDay(search.createdTo) ? { createdTo: normalizeDay(search.createdTo) } : {}),
    ...(sort && sort !== DEFAULT_THREAD_DIRECTORY_SORT ? { sort } : {}),
    ...(page ? { page } : {}),
  };
}

/**
 * A calendar day as the instant its range starts or ends.
 *
 * The columns hold ISO instants, so "to the 11th" has to mean the last millisecond of the 11th or
 * the filter would silently exclude everything that happened that day. Local time, not UTC: the
 * reader picked a day on their calendar, not a UTC window.
 */
const dayStart = (day: string): string => new Date(`${day}T00:00:00`).toISOString();
const dayEnd = (day: string): string => new Date(`${day}T23:59:59.999`).toISOString();

/** The URL's view of the filters, as the request the server takes. */
export function toThreadDirectoryInput(search: ThreadDirectoryRouteSearch): ThreadDirectoryInput {
  const state = search.state ?? DEFAULT_THREAD_DIRECTORY_STATE;
  return {
    filters: {
      state,
      ...(search.q ? { query: search.q } : {}),
      ...(search.projectId ? { projectId: search.projectId } : {}),
      ...(search.userId ? { userId: search.userId } : {}),
      ...(search.pr ? { hasPullRequest: search.pr === "with" } : {}),
      ...(search.activityFrom ? { activityFrom: dayStart(search.activityFrom) } : {}),
      ...(search.activityTo ? { activityTo: dayEnd(search.activityTo) } : {}),
      ...(search.createdFrom ? { createdFrom: dayStart(search.createdFrom) } : {}),
      ...(search.createdTo ? { createdTo: dayEnd(search.createdTo) } : {}),
    },
    sort: search.sort ?? DEFAULT_THREAD_DIRECTORY_SORT,
    page: search.page ?? 1,
    perPage: THREAD_DIRECTORY_DEFAULT_PER_PAGE,
  };
}
